/**
 * Modal para agregar items al presupuesto
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiX, FiEdit } from 'react-icons/fi';
import { itemPresupuestoVentaService, itemPresupuestoCostoService, categoriaService, ordenCompraService, detalleOrdenCompraService } from '../services/onebaseService';
import CrearOrdenCompraOneBaseModal from './CrearOrdenCompraOneBaseModal';
import './AgregarItemModal.css';

import Loading from './Loading';
const AgregarItemModal = ({ presupuesto, isOpen, onClose, onItemAdded, tipoPresupuesto = 'venta', itemParaEditar = null }) => {
  const [categorias, setCategorias] = useState([]);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [formData, setFormData] = useState({
    categoria: '',
    descripcion: '',
    cantidad: 1,
    cantidad_dias: 1,
    unidad: 'unidad',
    precio_unitario: 0,
    costo_unitario: '',
    markup: '',
    status: 'activo',
  });
  const [showItemForm, setShowItemForm] = useState(false);
  const [modoSeleccion, setModoSeleccion] = useState(false);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);
  const [showCrearOCModal, setShowCrearOCModal] = useState(false);
  const [ordenesCompra, setOrdenesCompra] = useState([]);
  const [detallesOC, setDetallesOC] = useState([]);

  useEffect(() => {
    if (isOpen) {
      cargarDatos();
      // Si hay un item para editar, cargar sus datos
      if (itemParaEditar) {
        setFormData({
          categoria: itemParaEditar.categoria || '',
          descripcion: itemParaEditar.descripcion,
          cantidad: itemParaEditar.cantidad,
          cantidad_dias: itemParaEditar.cantidad_dias,
          unidad: itemParaEditar.unidad,
          precio_unitario: itemParaEditar.precio_unitario,
          costo_unitario: itemParaEditar.costo_unitario || '',
          markup: itemParaEditar.markup || '',
          status: itemParaEditar.status,
        });
        setEditingItemId(itemParaEditar.id);
        setShowItemForm(true);
      } else {
        // Si no hay item para editar, mostrar formulario vacío
        setShowItemForm(true);
      }
    } else {
      // Reset form when modal closes
      setShowItemForm(false);
      resetForm();
    }
  }, [isOpen, presupuesto, itemParaEditar]);

  const cargarDatos = async () => {
    try {
      // Cargar categorías
      const categResponse = await categoriaService.getAll({ status: 'activo' });
      const categData = Array.isArray(categResponse.data)
        ? categResponse.data
        : (categResponse.data.results || []);
      setCategorias(categData);

      // Cargar items existentes
      cargarItems();
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar categorías');
    }
  };

  const cargarItems = async () => {
    setLoadingItems(true);
    try {
      const service = tipoPresupuesto === 'venta'
        ? itemPresupuestoVentaService
        : itemPresupuestoCostoService;

      const response = await service.getAll({
        presupuesto_venta: presupuesto.id,
        status: 'activo',
        limit: 1000
      });
      const itemsData = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setItems(itemsData);

      // Cargar órdenes de compra solo para presupuestos de costo
      if (tipoPresupuesto === 'costo') {
        await cargarOrdenesCompra();
      }
    } catch (error) {
      console.error('Error al cargar items:', error);
      toast.error('Error al cargar items');
    } finally {
      setLoadingItems(false);
    }
  };

  const cargarOrdenesCompra = async () => {
    try {
      // Cargar órdenes de compra del presupuesto
      const ocResponse = await ordenCompraService.getAll({
        presupuesto_costo: presupuesto.id,
        status: 'activo',
        limit: 1000
      });
      const ocData = Array.isArray(ocResponse.data)
        ? ocResponse.data
        : (ocResponse.data.results || []);

      console.log('📦 Órdenes de compra cargadas:', ocData.length);
      setOrdenesCompra(ocData);

      if (ocData.length === 0) {
        setDetallesOC([]);
        return;
      }

      // Cargar todos los detalles de OC sin filtrar por orden específica
      const todosDetallesResponse = await detalleOrdenCompraService.getAll({
        status: 'activo',
        limit: 1000
      });

      const todosLosDetalles = Array.isArray(todosDetallesResponse.data)
        ? todosDetallesResponse.data
        : (todosDetallesResponse.data.results || []);

      // Filtrar solo los detalles que pertenecen a las OCs de este presupuesto
      const idsOCs = ocData.map(oc => oc.id);
      const detallesFiltrados = todosLosDetalles.filter(detalle =>
        idsOCs.includes(detalle.orden_compra)
      );

      console.log('📋 Detalles de OC cargados:', detallesFiltrados.length);
      console.log('📋 Detalles:', detallesFiltrados);
      setDetallesOC(detallesFiltrados);
    } catch (error) {
      console.error('Error al cargar órdenes de compra:', error);
      // No mostrar error al usuario ya que es información adicional
    }
  };

  const calcularPrecioUnitario = (costo, markup) => {
    if (!costo || !markup) return 0;
    return costo * (1 + markup / 100);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Conversión de tipos
    let newValue = value;
    if (['cantidad', 'cantidad_dias', 'precio_unitario', 'costo_unitario', 'markup'].includes(name)) {
      newValue = value === '' ? '' : parseFloat(value) || 0;
    }

    let updatedFormData = {
      ...formData,
      [name]: newValue,
    };

    // Calcular precio_unitario automáticamente cuando cambia costo o markup
    if ((name === 'costo_unitario' || name === 'markup') && updatedFormData.costo_unitario && updatedFormData.markup) {
      updatedFormData.precio_unitario = calcularPrecioUnitario(
        updatedFormData.costo_unitario,
        updatedFormData.markup
      );
    }

    setFormData(updatedFormData);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();

    if (!formData.descripcion.trim()) {
      toast.error('La descripción es requerida');
      return;
    }

    if (formData.cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    if (formData.precio_unitario < 0) {
      toast.error('El precio unitario no puede ser negativo');
      return;
    }

    try {
      const service = tipoPresupuesto === 'venta'
        ? itemPresupuestoVentaService
        : itemPresupuestoCostoService;

      const dataToSend = {
        presupuesto_venta: presupuesto.id,
        descripcion: formData.descripcion,
        cantidad: formData.cantidad,
        cantidad_dias: formData.cantidad_dias,
        unidad: formData.unidad,
        precio_unitario: formData.precio_unitario,
        status: formData.status,
      };

      // Agregar campos opcionales si existen
      if (formData.categoria) {
        dataToSend.categoria = formData.categoria;
      }
      if (formData.costo_unitario) {
        dataToSend.costo_unitario = formData.costo_unitario;
      }
      if (formData.markup) {
        dataToSend.markup = formData.markup;
      }

      if (editingItemId) {
        // Actualizar item existente
        await service.update(editingItemId, dataToSend);
        toast.success('Item actualizado correctamente');
      } else {
        // Crear nuevo item
        await service.create(dataToSend);
        toast.success('Item agregado correctamente');
      }

      resetForm();
      cargarItems();

      if (onItemAdded) {
        onItemAdded();
      }
    } catch (error) {
      console.error('Error al guardar item:', error);
      toast.error(editingItemId ? 'Error al actualizar item' : 'Error al agregar item');
    }
  };

  const handleEditItem = (item) => {
    setFormData({
      categoria: item.categoria || '',
      descripcion: item.descripcion,
      cantidad: item.cantidad,
      cantidad_dias: item.cantidad_dias,
      unidad: item.unidad,
      precio_unitario: item.precio_unitario,
      costo_unitario: item.costo_unitario || '',
      markup: item.markup || '',
      status: item.status,
    });
    setEditingItemId(item.id);
    setShowItemForm(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('¿Eliminar este item?')) {
      return;
    }

    try {
      const service = tipoPresupuesto === 'venta'
        ? itemPresupuestoVentaService
        : itemPresupuestoCostoService;

      await service.delete(itemId);
      toast.success('Item eliminado');
      cargarItems();

      if (onItemAdded) {
        onItemAdded();
      }
    } catch (error) {
      console.error('Error al eliminar item:', error);
      toast.error('Error al eliminar item');
    }
  };

  const resetForm = () => {
    setFormData({
      categoria: '',
      descripcion: '',
      cantidad: 1,
      cantidad_dias: 1,
      unidad: 'unidad',
      precio_unitario: 0,
      costo_unitario: '',
      markup: '',
      status: 'activo',
    });
    setEditingItemId(null);
    setShowItemForm(false);
  };

  const handleToggleModoSeleccion = () => {
    setModoSeleccion(!modoSeleccion);
    if (modoSeleccion) {
      setItemsSeleccionados([]);
    }
  };

  const handleToggleItemSeleccion = (item) => {
    setItemsSeleccionados(prev => {
      const isSelected = prev.find(i => i.id === item.id);
      if (isSelected) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleCrearOrdenCompra = () => {
    if (itemsSeleccionados.length === 0) {
      toast.error('Selecciona al menos un item');
      return;
    }
    setShowCrearOCModal(true);
  };

  const handleOCCreated = async () => {
    setItemsSeleccionados([]);
    setModoSeleccion(false);
    setShowCrearOCModal(false);
    // Recargar items y órdenes de compra
    await cargarItems();
  };

  const calcularSubtotal = (cantidad, cantidadDias, precio) => {
    return (cantidad || 0) * (cantidadDias || 0) * (precio || 0);
  };

  const calcularCostoTotal = () => {
    if (!items || items.length === 0) return 0;
    return items.reduce((total, item) => {
      const costo = parseFloat(item.costo_unitario) || 0;
      const cantidad = parseFloat(item.cantidad) || 0;
      const dias = parseFloat(item.cantidad_dias) || 0;
      const costoItem = cantidad * dias * costo;
      return total + costoItem;
    }, 0);
  };

  const calcularSubtotalTotal = () => {
    if (!items || items.length === 0) return 0;
    return items.reduce((total, item) => {
      const subtotal = parseFloat(item.subtotal) || 0;
      return total + subtotal;
    }, 0);
  };

  const calcularMargenTotal = () => {
    const subtotalTotal = calcularSubtotalTotal();
    const costoTotal = calcularCostoTotal();
    return subtotalTotal - costoTotal;
  };

  const calcularMargenPorcentaje = () => {
    const costoTotal = calcularCostoTotal();
    if (costoTotal <= 0) return 0;
    const margenTotal = calcularMargenTotal();
    return (margenTotal / costoTotal) * 100;
  };

  const calcularCantidadSolicitada = (item) => {
    // Sumar las cantidades de todos los detalles de OC que coincidan con este item
    const detallesCoincidentes = detallesOC.filter(detalle =>
      detalle.descripcion === item.descripcion
    );

    const total = detallesCoincidentes.reduce((total, detalle) =>
      total + parseFloat(detalle.cantidad || 0), 0
    );

    if (detallesCoincidentes.length > 0) {
      console.log(`🔍 Item: "${item.descripcion}"`, {
        detallesEncontrados: detallesCoincidentes.length,
        cantidadTotal: total,
        detalles: detallesCoincidentes
      });
    }

    return total;
  };

  const calcularCantidadDisponible = (item) => {
    const cantidadTotal = parseFloat(item.cantidad) || 0;
    const cantidadSolicitada = calcularCantidadSolicitada(item);
    return cantidadTotal - cantidadSolicitada;
  };

  const calcularSubtotalSolicitado = (item) => {
    // Sumar los subtotales (cantidad * precio_unitario) de todos los detalles de OC para este item
    return detallesOC
      .filter(detalle => detalle.descripcion === item.descripcion)
      .reduce((total, detalle) => {
        const cantidad = parseFloat(detalle.cantidad || 0);
        const precioUnitario = parseFloat(detalle.precio_unitario || 0);
        return total + (cantidad * precioUnitario);
      }, 0);
  };

  const calcularPrecioUnitarioSolicitado = (item) => {
    // Calcular el precio unitario promedio ponderado de las OCs para este item
    const detallesItem = detallesOC.filter(detalle => detalle.descripcion === item.descripcion);

    if (detallesItem.length === 0) return 0;

    const totalCantidad = detallesItem.reduce((sum, detalle) =>
      sum + parseFloat(detalle.cantidad || 0), 0
    );

    if (totalCantidad === 0) return 0;

    const totalMonto = detallesItem.reduce((sum, detalle) => {
      const cantidad = parseFloat(detalle.cantidad || 0);
      const precioUnitario = parseFloat(detalle.precio_unitario || 0);
      return sum + (cantidad * precioUnitario);
    }, 0);

    return totalMonto / totalCantidad; // Precio promedio ponderado
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(value || 0);
  };

  // Determinar si se deben mostrar las acciones en presupuesto de venta
  const mostrarAccionesVenta = () => {
    if (tipoPresupuesto !== 'venta') return false;
    const estadosBloqueo = ['aprobacion_interna', 'enviado_aprobacion_cliente', 'aprobacion_cliente', 'presupuesto_costo_creado'];
    return !estadosBloqueo.includes(presupuesto.estado);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content agregar-item-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Items - {presupuesto.proyecto_nombre}</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          {/* Listado de items */}
          {!showItemForm && (
          <div className="items-section">
            <div className="section-header">
              <h3>Items agregados ({items.length})</h3>
              <div className="header-actions">
                {/* Botones de OC solo para presupuestos de costo */}
                {tipoPresupuesto === 'costo' && items.length > 0 && (
                  <div className="oc-buttons">
                    <button
                      className={`btn-sm ${modoSeleccion ? 'btn-secondary' : 'btn-info'}`}
                      onClick={handleToggleModoSeleccion}
                    >
                      {modoSeleccion ? '✕ Cancelar' : '☑ Seleccionar items'}
                    </button>
                    {modoSeleccion && itemsSeleccionados.length > 0 && (
                      <button
                        className="btn-success btn-sm"
                        onClick={handleCrearOrdenCompra}
                      >
                        ➕ Orden Compra ({itemsSeleccionados.length})
                      </button>
                    )}
                  </div>
                )}
                {!showItemForm && (tipoPresupuesto === 'costo' || mostrarAccionesVenta()) && (
                  <button
                    className="btn-primary btn-sm"
                    onClick={() => setShowItemForm(true)}
                  >
                    <FiPlus /> Agregar Item
                  </button>
                )}
              </div>
            </div>

            {loadingItems ? (
              <Loading fullScreen={false} size="sm" mensaje="Cargando items" />
            ) : items.length > 0 ? (
              <div className="items-table">
                <table>
                  <thead>
                    {tipoPresupuesto === 'costo' ? (
                      <tr>
                        {modoSeleccion && <th></th>}
                        <th>Descripción</th>
                        <th>Unidad</th>
                        <th>Días</th>
                        <th>Presupuestado</th>
                        <th>Solicitado</th>
                        <th>Disponible</th>
                        <th>$ Presupuesto</th>
                        <th>$ Solicitado</th>
                        <th>Markup %</th>
                        <th>Subtotal Presupuestado</th>
                        <th>Subtotal Solicitado</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>Descripción</th>
                        <th>Cantidad</th>
                        <th>Días</th>
                        <th>Unidad</th>
                        <th>Costo Unit.</th>
                        <th>Markup %</th>
                        <th>Precio</th>
                        <th>Subtotal</th>
                        {mostrarAccionesVenta() && <th>Acciones</th>}
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      tipoPresupuesto === 'costo' ? (
                        <tr
                          key={item.id}
                          className={itemsSeleccionados.some(i => i.id === item.id) ? 'item-selected' : ''}
                        >
                          {modoSeleccion && (
                            <td>
                              <input
                                type="checkbox"
                                checked={itemsSeleccionados.some(i => i.id === item.id)}
                                onChange={() => handleToggleItemSeleccion(item)}
                                className="checkbox-seleccion"
                              />
                            </td>
                          )}
                          <td>{item.descripcion}</td>
                          <td>{item.unidad}</td>
                          <td>{item.cantidad_dias}</td>
                          <td>{item.cantidad}</td>
                          <td style={{ fontWeight: '600', color: '#f59e0b' }}>
                            {calcularCantidadSolicitada(item).toFixed(2)}
                          </td>
                          <td style={{
                            fontWeight: '600',
                            color: calcularCantidadDisponible(item) >= 0 ? '#10b981' : '#ef4444'
                          }}>
                            {calcularCantidadDisponible(item).toFixed(2)}
                          </td>
                          <td>{item.costo_unitario ? formatCurrency(item.costo_unitario) : '-'}</td>
                          <td style={{ fontWeight: '600', color: calcularPrecioUnitarioSolicitado(item) > 0 ? '#3b82f6' : 'inherit' }}>
                            {calcularPrecioUnitarioSolicitado(item) > 0
                              ? formatCurrency(calcularPrecioUnitarioSolicitado(item))
                              : formatCurrency(item.precio_unitario)
                            }
                          </td>
                          <td>{item.markup ? `${item.markup}%` : '-'}</td>
                          <td>{formatCurrency(item.subtotal)}</td>
                          <td style={{ fontWeight: '600', color: '#3b82f6' }}>
                            {formatCurrency(calcularSubtotalSolicitado(item))}
                          </td>
                        </tr>
                      ) : (
                        <tr key={item.id}>
                          <td>{item.descripcion}</td>
                          <td>{item.cantidad}</td>
                          <td>{item.cantidad_dias}</td>
                          <td>{item.unidad}</td>
                          <td>{item.costo_unitario ? formatCurrency(item.costo_unitario) : '-'}</td>
                          <td>{item.markup ? `${item.markup}%` : '-'}</td>
                          <td>{formatCurrency(item.precio_unitario)}</td>
                          <td>{formatCurrency(item.subtotal)}</td>
                          {mostrarAccionesVenta() && (
                            <td className="actions">
                              <button
                                className="btn-icon"
                                onClick={() => handleEditItem(item)}
                                title="Editar"
                              >
                                <FiEdit />
                              </button>
                              <button
                                className="btn-icon danger"
                                onClick={() => handleDeleteItem(item.id)}
                                title="Eliminar"
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          )}
                        </tr>
                      )
                    ))}
                  </tbody>
                  <tfoot>
                    {tipoPresupuesto === 'costo' ? (
                      <tr className="totals-row">
                        {modoSeleccion && <td></td>}
                        <td colSpan="9" style={{ textAlign: 'right', paddingRight: '12px' }}>
                          <strong>Totales:</strong>
                        </td>
                        <td style={{ fontWeight: '600', backgroundColor: '#e8f5e9' }}>
                          {formatCurrency(calcularSubtotalTotal())}
                        </td>
                        <td style={{ fontWeight: '600', backgroundColor: '#dbeafe', color: '#3b82f6' }}>
                          {formatCurrency(items.reduce((total, item) =>
                            total + calcularSubtotalSolicitado(item), 0
                          ))}
                        </td>
                      </tr>
                    ) : (
                      <tr className="totals-row">
                        <td colSpan="4" style={{ textAlign: 'right', paddingRight: '12px' }}>
                          <strong>Totales:</strong>
                        </td>
                        <td style={{ fontWeight: '600' }}>
                          {formatCurrency(calcularCostoTotal())}
                        </td>
                        <td style={{ fontWeight: '600' }}>
                          {isNaN(calcularMargenPorcentaje()) ? '0' : calcularMargenPorcentaje().toFixed(2)}%
                        </td>
                        <td style={{ fontWeight: '600' }}>
                          {/* Columna de Precio no se suma */}
                          -
                        </td>
                        <td style={{ fontWeight: '600', backgroundColor: '#e8f5e9' }}>
                          {formatCurrency(calcularSubtotalTotal())}
                        </td>
                        {mostrarAccionesVenta() && <td></td>}
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No hay items agregados</p>
              </div>
            )}

            {/* Totales */}
            {items.length > 0 && (
              <div className="items-summary">
                <div className="summary-item">
                  <strong>Subtotal:</strong>
                  <span>{formatCurrency(presupuesto.subtotal || 0)}</span>
                </div>
                {presupuesto.descuento_total > 0 && (
                  <div className="summary-item">
                    <strong>Descuento:</strong>
                    <span>-{formatCurrency(presupuesto.descuento_total)}</span>
                  </div>
                )}
                {presupuesto.iva > 0 && (
                  <div className="summary-item">
                    <strong>IVA ({presupuesto.iva_porcentaje}%):</strong>
                    <span>{formatCurrency(presupuesto.iva)}</span>
                  </div>
                )}
                <div className="summary-item total">
                  <strong>Total:</strong>
                  <span>{formatCurrency(presupuesto.total_presupuesto || 0)}</span>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Formulario para agregar item */}
          {showItemForm && (
            <div className="form-section">
              <h3>{editingItemId ? 'Editar Item' : 'Nuevo Item'}</h3>
              <form onSubmit={handleAddItem}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Descripción *</label>
                    <input
                      type="text"
                      name="descripcion"
                      value={formData.descripcion}
                      onChange={handleInputChange}
                      placeholder="Ej: Diseño gráfico para campaña"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Categoría</label>
                    <select
                      name="categoria"
                      value={formData.categoria}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccionar...</option>
                      {categorias.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Cantidad *</label>
                    <input
                      type="number"
                      name="cantidad"
                      value={formData.cantidad}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Cantidad Días</label>
                    <input
                      type="number"
                      name="cantidad_dias"
                      value={formData.cantidad_dias}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="Ej: 5, 10, 15"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Unidad</label>
                    <input
                      type="text"
                      name="unidad"
                      value={formData.unidad}
                      onChange={handleInputChange}
                      placeholder="Ej: horas, días, unidades"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Costo Unitario (opcional)</label>
                    <input
                      type="number"
                      name="costo_unitario"
                      value={formData.costo_unitario}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="Costo interno"
                    />
                  </div>
                  <div className="form-group">
                    <label>Markup % (opcional)</label>
                    <input
                      type="number"
                      name="markup"
                      value={formData.markup}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="Margen de ganancia"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Precio Unitario {formData.costo_unitario && formData.markup ? '(calculado)' : '*'}</label>
                    <input
                      type="number"
                      name="precio_unitario"
                      value={formData.precio_unitario}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      disabled={!!(formData.costo_unitario && formData.markup)}
                      className={formData.costo_unitario && formData.markup ? 'disabled-input' : ''}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Subtotal</label>
                    <input
                      type="text"
                      value={formatCurrency(calcularSubtotal(formData.cantidad, formData.cantidad_dias, formData.precio_unitario))}
                      disabled
                      className="disabled-input"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingItemId ? 'Guardar cambios' : 'Agregar Item'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={resetForm}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear orden de compra */}
      {showCrearOCModal && (
        <CrearOrdenCompraOneBaseModal
          presupuesto={presupuesto}
          itemsSeleccionados={itemsSeleccionados}
          onClose={() => setShowCrearOCModal(false)}
          onCreated={handleOCCreated}
        />
      )}
    </div>
  );
};

export default AgregarItemModal;
